// «Настройки» (decision 031 п.3, um/2026-10-29/01 п.7, п.12): one section behind the avatar — the profile, the account and its plan,
// the team, the structure and the groups of settings — with its own left menu in place of the second menu (route /settings/<item>).
// The menu is grouped («Личное», «Аккаунт», «Правила», «Функции»), every page has the same head and width, and the search of the
// palette finds items and settings (decision 038 п.3, DESIGN.md «Настройки»).

import { Fragment, useEffect, type ComponentType } from 'react';
import { useSync } from '../sync/react.tsx';
import { navigate } from '../shell/router.ts';
import { screenOf } from '../shell/screens.ts';
import { openSearch, useScreenSearch, type ScreenHit } from '../shell/screen-search.ts';
import type { TopAction } from '../ui/TopBar.tsx';
import { NavRow } from '../shell/SideNav.tsx';
import { TeamScreen } from '../team/TeamScreen.tsx';
import { TaxonomiesScreen } from '../schema/TaxonomiesScreen.tsx';
import { Button } from '../ui/primitives.tsx';
import { ListEmpty } from '../ui/ListReader.tsx';
import { Page } from '../ui/Page.tsx';
import { ProfilePane } from './ProfilePane.tsx';
import { FEATURES, SettingsGroup, focusSetting, useManage, useSettingGroups, useVisibleDecls } from './SettingsGroup.tsx';
import { OLD_SLUGS } from './group-slugs.ts';
import { searchSettings, type SettingsHit } from './rows.ts';
import s from './settings.module.css';

// A group of settings is data, not a component made on each render: a new component type would remount the page and drop
// what the person is typing whenever the shell re-renders (presence, own edits — review nav, P1-1).
interface Item {
  key: string; label: string; icon: string; part: Part; render?: ComponentType; group?: string; width?: 'collection' | 'full';
  /** The line under the head of the page: what is set here. */
  sub?: string;
  /** Words the search finds the item by, besides its name. */
  words?: string;
}
type Part = 'Личное' | 'Аккаунт' | 'Правила' | 'Функции';

/** Screens that used to be rail sections (/s/<key>) and the item of «Настройки» that took their place. */
const MOVED: Record<string, string> = { team: 'team', taxonomies: 'schema', settings: '' };
export const movedScreen = (key: string): string | undefined => (key in MOVED ? `/settings${MOVED[key] ? `/${MOVED[key]}` : ''}` : undefined);

/** What a group's page says under its head, by its address: a group of an app without a line here has none. */
const GROUP_SUB: Record<string, string> = {
  general: 'Общие правила пространства.',
  records: 'Как люди правят записи. Правило можно задать для всех, для роли или человека — во всех разделах или в одном.',
  tables: 'Личная настройка: каждый может выбрать своё значение, здесь — значение для всех.',
  files: 'Как открываются ссылки на файлы записей.',
  revisions: 'История изменений записей. Можно задать для всех разделов или для одного.',
  support: 'Доступ поддержки ConfDeck к пространству.',
};

/** Items the person sees: «Команда» and «Структура» are the owner's (the server leaves their sections out of the menu for others). */
function useItems(): Item[] {
  const client = useSync();
  const groups = useSettingGroups();
  const manage = useManage();
  const has = (k: string) => client.menu.some((m) => m.key === k);
  // The account and its plan is a screen of the SaaS part; the item shows once that screen is there, and only to those the
  // server shows it to — the owner and people with the right «Настройки» (B3: no item that opens a refusal).
  const account = manage ? screenOf('account') : undefined;
  return [
    { key: 'profile', label: 'Профиль', icon: 'user', part: 'Личное', render: ProfilePane, words: 'имя аватар пароль email почта тема шрифт плотность устройства выйти владение выгрузка данные' },
    ...(account ? [{ key: 'account', label: 'Аккаунт и тариф', icon: 'money', part: 'Аккаунт' as const, render: account, words: 'тариф оплата продлить место платежи заморозить удалить аккаунт использование' }] : []),
    ...(has('team') ? [{ key: 'team', label: 'Команда', icon: 'users', part: 'Аккаунт' as const, render: TeamScreen, width: 'full' as const, words: 'люди роли права пригласить доступ' }] : []),
    ...(has('schema') ? [{ key: 'schema', label: 'Структура', icon: 'tag', part: 'Аккаунт' as const, render: TaxonomiesScreen, width: 'full' as const, words: 'таксономии значения метки воронки этапы' }] : []),
    ...groups.map((g): Item => (g.name === FEATURES
      ? { key: g.slug, label: g.name, icon: 'zap', part: 'Функции', group: g.name, sub: 'Выключенная функция пропадает из меню, карточек, сайта и сценариев. Данные остаются: включите — всё вернётся. Основа модуля работает всегда.', words: 'модули функции включить выключить' }
      : { key: g.slug, label: g.name, icon: 'gear', part: 'Правила', group: g.name, sub: GROUP_SUB[g.slug] })),
  ];
}

/** The item by its address: its key, an old numbered address of a group, or an old address by the group's name (/settings/Записи). */
export function pickItem<T extends { key: string; label: string }>(items: T[], key: string | null): T | null {
  if (!key) return items[0] ?? null;
  const k = OLD_SLUGS[key] ?? key;
  return items.find((i) => i.key === k || safeDecode(k) === i.label) ?? null;
}
const safeDecode = (k: string) => { try { return decodeURIComponent(k); } catch { return k; } };

/** The head of the left column: the column is the menu of «Настройки», the page names the item (B15). */
export const useSettingsTitle = (_section: string | null) => 'Настройки';

export function SettingsNav({ section }: { section: string | null }) {
  const items = useItems();
  const cur = pickItem(items, section);
  let last: Part | null = null;
  return (
    <>
      {items.map((i) => {
        const head = i.part !== last ? i.part : null;
        last = i.part;
        return (
          <Fragment key={i.key}>
            {head && <div className={s.navHead} role="presentation">{head}</div>}
            <NavRow icon={i.icon} label={i.label} on={i === cur} onClick={() => navigate(`/settings/${i.key}`)} />
          </Fragment>
        );
      })}
    </>
  );
}

/** What the search of «Настройки» finds for a query: an item opens, a setting opens its group and lights its row. */
export function useSettingsSearch(): (query: string) => (SettingsHit & { run: () => void })[] {
  const items = useItems();
  const decls = useVisibleDecls();
  return (q) => searchSettings(q, items, decls).map((h) => ({ ...h, run: () => { navigate(`/settings/${h.item}`); if (h.key) focusSetting(h.key); } }));
}
/** «Найти настройку» in the top line: the search of «Настройки» is the global one, with the settings first (R-U23). */
const FIND: TopAction[] = [{ key: 'find', label: 'Найти настройку', icon: 'search', iconOnly: true, title: 'Найти настройку (⌘K)', onClick: openSearch }];
export const settingsHitId = (h: SettingsHit) => `set:${h.item}:${h.key ?? ''}`;

export function SettingsPage({ section }: { section: string | null }) {
  const client = useSync();
  const items = useItems();
  const cur = pickItem(items, section);
  const search = useSettingsSearch();
  // On a page of «Настройки» the palette finds the settings first (R-U23, decision 038 п.3).
  useScreenSearch((q) => search(q).map((h): ScreenHit => ({ id: settingsHitId(h), label: h.label, hint: h.key ? h.itemLabel : 'Настройки', icon: h.key ? 'gear' : 'settings', run: h.run })));
  // A bare or an old address shows its item and puts the item's key in the address; an unknown one stays as it is (B5).
  useEffect(() => { if (cur && section !== cur.key) navigate(`/settings/${cur.key}`, { replace: true }); }, [section, cur?.key]);
  if (!cur) {
    // The groups come with the header: until it is here an address of a group is not «unknown» yet.
    if (!client.header) return <Page title="Настройки"><p className={s.lead}>Загрузка…</p></Page>;
    return (
      <Page title="Пункт не найден">
        <ListEmpty icon="settings" text={`В «Настройках» нет пункта «${safeDecode(section ?? '')}»`}
          hint="Его могли переименовать, или выключена функция, к которой он относится." action={<Button onClick={() => navigate('/settings/profile')}>Открыть профиль</Button>} />
      </Page>
    );
  }
  // One body for every page of «Настройки» (um/2026-10-30/03 п.10): the same head, width and paddings. A page with a table and its
  // cards in the stack (people, the structure — R-U27) takes the whole width, a page of blocks is a single page in the centre.
  if (cur.group !== undefined) return <Page key={cur.key} title={cur.label} sub={cur.sub} actions={FIND}><SettingsGroup group={cur.group} /></Page>;
  const C = cur.render!;
  return <Page key={cur.key} title={cur.label} sub={cur.sub} width={cur.width ?? 'single'} actions={cur.width === 'full' ? undefined : FIND}><C /></Page>;
}
